import moment, { Moment } from 'moment-timezone';
import { deburr } from 'lodash';
import specialistActCodes from '../data/specialist-act-codes.json';

import SpecialtyName, { getUserSpecialtyId } from '../core/doctor/domain/SpecialtyName';
import { RamqSpecialistActCodeDTO } from '../medyx-core/types/ramq';

export interface Unit {
  role: string;
  units: string;
}

export enum TimedAttributeKey {
  isAddendum8 = 'isAddendum8',
  isPainDay = 'isPainDay',
  isPainDayExclusive = 'isPainDayExclusive',
  isPainDayCompatible = 'isPainDayCompatible'
}

export class RAMQCodes {
  private static ramqCodes: Map<string, RamqSpecialistActCodeDTO>;

  static get codesMap() {
    if (!this.ramqCodes) {
      this.ramqCodes = this.buildRamqDict();
    }

    return this.ramqCodes;
  }

  private static buildRamqDict(): Map<string, RamqSpecialistActCodeDTO> {
    const codesMap = new Map<string, RamqSpecialistActCodeDTO>();
    (specialistActCodes as RamqSpecialistActCodeDTO[]).forEach((entry) => codesMap.set(entry.code, entry));
    return codesMap;
  }

  public static search(
    searchTerm: string,
    serviceDate: Moment | number | undefined = undefined,
    maxResult: number = 21
  ): RamqSpecialistActCodeDTO[] {
    const searchTokens = deburr(searchTerm).toLowerCase().split(' ');
    const date = serviceDate ? moment(serviceDate) : null;

    const foundCodes: RamqSpecialistActCodeDTO[] = [];
    for (const code of Array.from(this.codesMap.values())) {
      const descriptionLower = deburr(code.description).toLowerCase();
      if (
        (!date || this.isCodeEffectiveOn(code, date)) &&
        searchTokens.every((searchToken) => code.code === searchToken || descriptionLower.includes(searchToken))
      ) {
        foundCodes.push(code);
      }
      if (foundCodes.length === maxResult) {
        break;
      }
    }

    return foundCodes;
  }

  public static get(code: string): RamqSpecialistActCodeDTO | undefined {
    return this.codesMap.get(code);
  }

  public static getTimedAttribute<T>(
    ramqCode: RamqSpecialistActCodeDTO,
    attributeKey: TimedAttributeKey,
    date: Moment | number
  ) {
    const attribute = ramqCode.timedAttributes.find(({ key }) => attributeKey === key);

    if (!attribute) {
      return undefined;
    }

    if (attribute.effectiveStartDate && moment(date).isBefore(attribute.effectiveStartDate)) {
      return undefined;
    }

    if (attribute.effectiveEndDate && moment(date).isAfter(attribute.effectiveEndDate)) {
      return undefined;
    }

    return attribute.value as T;
  }

  public static isCodeEffectiveOn(
    ramqCode: RamqSpecialistActCodeDTO,
    serviceDate: Moment | number | undefined
  ): boolean {
    const date = moment(serviceDate); // When serviceDate is undefined, fallback on current date

    if (!ramqCode.effectiveEndDate) {
      return date.isSameOrAfter(ramqCode.effectiveStartDate);
    }

    return date.isBetween(
      moment(ramqCode.effectiveStartDate),
      moment(ramqCode.effectiveEndDate),
      undefined, // Granularity
      '[]' // Inclusivity
    );
  }

  public static getRolesForCodeEffectiveOnAndForSpecialty(
    code: string,
    serviceDate: Moment | number,
    specialtyName?: SpecialtyName
  ): string[] {
    const FALLBACK_ROLE_LIST = ['1'];
    const ramqCode = this.get(code);

    if (ramqCode === undefined) {
      return FALLBACK_ROLE_LIST;
    }

    const isForUserSpecialty = ({ specialty }) =>
      specialtyName === undefined || specialty === null || specialty === getUserSpecialtyId(specialtyName);
    const isEffective = ({ effectiveEndDate, effectiveStartDate }) => {
      if (!effectiveEndDate) {
        return moment(serviceDate).isSameOrAfter(effectiveStartDate);
      }

      return moment(serviceDate).isBetween(
        moment(effectiveStartDate),
        moment(effectiveEndDate),
        undefined, // Granularity
        '(]' // Inclusivity
      );
    };
    const roleMetadata = ramqCode.roleMetadata || [];
    const roles = roleMetadata
      .filter(isForUserSpecialty)
      .filter(isEffective)
      .map(({ role }) => role)
      .sort();

    return roles.length === 0 ? FALLBACK_ROLE_LIST : roles;
  }

  public static getRoleUnitsForSpecialty(code: string, specialtyName: SpecialtyName): Unit[] {
    const ramqCode = this.get(code);

    if (ramqCode === undefined) {
      return [];
    }

    const { unitsBySpecialty } = ramqCode;

    return unitsBySpecialty[getUserSpecialtyId(specialtyName)] ?? unitsBySpecialty.default ?? [];
  }
}

export default RAMQCodes;
